import { defineStore } from 'pinia'
import { ref } from 'vue'

import api from '@axios'

import type { PaginationMeta, PatientHistoryParams, PatientHistoryRow, PatientSummary } from './types'

// PatientSummary is declared once, in ./types.ts (imported above) -- it's the
// normalized, frontend-facing shape. Do NOT redeclare it here: the backend's
// actual response is { patient, data }, not { patient, insurances } -- the
// mapping below is the one place that wire-shape/normalized-shape gap is
// bridged, and it only works if this file consumes the shared type rather
// than defining its own same-named one.

export const useIvPatientSummaryStore = defineStore('iv-patient-summary', () => {
  const summary = ref<PatientSummary | null>(null)
  const loading = ref(false)

  async function fetchSummary(patientUuid: string): Promise<PatientSummary> {
    loading.value = true
    try {
      // skipDataUnwrap: this endpoint's response is { patient, data: [...] }, not a
      // Laravel-resource-collection envelope -- without this flag the global axios
      // response interceptor (src/plugins/axios.ts) collapses response.data to
      // response.data.data, silently discarding `patient` before it ever reaches here.
      const { data } = await api.get(`/insurance/patients/${patientUuid}/insurance-summary`, { skipDataUnwrap: true })

      const result: PatientSummary = {
        patient: data.patient,
        insurances: data.data ?? [],
        second_plan: data.second_plan,
      }

      summary.value = result

      return result
    } finally {
      loading.value = false
    }
  }

  // VH-2337: one page of Patient History, grouped, filtered and sorted by the server. POST, not
  // GET: the search box is a patient name, which must not land in a query string (access logs,
  // browser history). Response has both `data` and `meta` keys, so the global axios unwrap
  // interceptor leaves it as a full { data, meta } envelope (see src/plugins/axios.ts).
  async function fetchHistory(params: PatientHistoryParams): Promise<{ data: PatientHistoryRow[]; meta: PaginationMeta }> {
    const { data } = await api.post('/insurance/patients/history', params)

    return data as { data: PatientHistoryRow[]; meta: PaginationMeta }
  }

  function reset() {
    summary.value = null
    loading.value = false
  }

  return { summary, loading, fetchSummary, fetchHistory, reset }
})
